import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowRight, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import { overviewStats, decisionTrend, regulatoryReadiness, recentDecisionRecords, riskEvents } from '@/data/mockData';

const RiskBadge = ({ tier }) => {
  const cls = tier === 'HIGH' ? 'risk-high' : tier === 'MEDIUM' ? 'risk-medium' : 'risk-low';
  return <span className={`${cls} text-[10px] font-mono px-2 py-0.5 rounded-full`}>{tier}</span>;
};

const posturePieData = [
  { value: overviewStats.aiPostureScore },
  { value: 100 - overviewStats.aiPostureScore },
];

const urgencyMessages = {
  global: 'EU AI Act Annex III obligations: August 2, 2026 · SOC 2 auditors increasingly reviewing AI governance evidence · US insurance renewals averaging +38% without AI documentation · NIST AI RMF required in federal AI procurement',
  us: 'SOC 2 auditors increasingly reviewing AI governance, change control, and Processing Integrity evidence for organizations using AI in service delivery · Cyber insurance renewals averaging +38% without AI governance documentation · SEC AI disclosure obligations active',
  ca: 'Canadian AIDA progressing through Parliament · ISO 42001 referenced by federal regulators · PIPEDA AI guidance enforced · OPC audit activity increasing · SOC 2 AI evidence coverage relevant for Canadian enterprises with US customers',
  eu: 'EU AI Act Annex III obligations: August 2, 2026 · Article 13 transparency requirements active for high-risk AI systems · ISO 42001 certification increasingly referenced by regulators',
};

function GovernanceAlerts() {
  const [alertsOpen, setAlertsOpen] = useState(false);
  return (
    <div className="rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/15 px-5 py-2.5" data-testid="governance-alerts">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle size={14} className="text-[#F59E0B] flex-shrink-0" />
          <span className="text-[10px] text-[#F59E0B]">2 governance alerts</span>
          <span className="text-[10px] text-[#94A3B8]">· Claude Sonnet 3.7 · Aug 2, 2026</span>
        </div>
        <button onClick={() => setAlertsOpen(!alertsOpen)} className="text-[10px] text-[#6366F1] hover:text-[#818cf8]">
          {alertsOpen ? 'Hide' : 'View →'}
        </button>
      </div>
      {alertsOpen && (
        <div className="mt-3 pt-3 border-t border-[#1E2A45] space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={12} className="text-[#F59E0B]" />
              <div>
                <div className="text-[10px] text-[#F8FAFC]">POTENTIAL LEGAL STATUS REVIEW REQUIRED</div>
                <div className="text-[9px] text-[#94A3B8]">Fine-tuning on Claude Sonnet 3.7 — legal review recommended before Aug 2, 2026.</div>
              </div>
            </div>
            <Link to="/dashboard/ai-inventory" className="text-[9px] text-[#F59E0B] hover:text-[#FBBF24] flex-shrink-0">Start Review →</Link>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info size={12} className="text-[#6366F1]" />
              <div>
                <div className="text-[10px] text-[#F8FAFC]">CLASSIFICATION EXPIRY APPROACHING</div>
                <div className="text-[9px] text-[#94A3B8]">GPT-4o Mini classification expires Jun 15, 2026 — 33 days.</div>
              </div>
            </div>
            <button onClick={() => toast.info('Scheduling classification review')} className="text-[9px] text-[#6366F1] hover:text-[#818cf8] flex-shrink-0">Schedule Review →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OverviewDashboard() {
  const [urgencyRegion, setUrgencyRegion] = useState('global');
  return (
    <div data-testid="overview-dashboard" className="space-y-6">
      {/* Executive Summary Block */}
      <div data-testid="executive-summary">
        <div className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-2">AI Governance Status · <span className="text-[#10B981]">Live</span> · Meridian Global</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-1"><div className="w-2 h-2 rounded-full bg-[#EF4444]" /><span className="text-[10px] text-[#475569] uppercase">Active exceptions</span></div>
            <div className="text-xl font-bold text-[#F8FAFC]">1</div>
            <div className="text-[10px] text-[#EF4444]">High-risk integrity warning · IN PROGRESS</div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-1"><div className="w-2 h-2 rounded-full bg-[#F59E0B]" /><span className="text-[10px] text-[#475569] uppercase">Decisions affected</span></div>
            <div className="text-xl font-bold text-[#F8FAFC]">143</div>
            <div className="text-[10px] text-[#F59E0B]">Sharing flagged upstream source</div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-1"><div className="w-2 h-2 rounded-full bg-[#F59E0B]" /><span className="text-[10px] text-[#475569] uppercase">Regulatory exposure</span></div>
            <div className="text-sm font-bold text-[#F8FAFC] mt-1">SOC 2 PI1 · EU AI Act Annex IV</div>
            <div className="text-[10px] text-[#F59E0B]">Potential deficiencies identified</div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-1"><div className="w-1.5 h-1.5 rounded-full bg-[#475569]" /><span className="text-[10px] text-[#475569] uppercase">Resolution ETA</span></div>
            <div className="text-lg font-bold text-[#F8FAFC]">Apr 19, 2026</div>
            <div className="text-[10px] text-[#64748B]">4 days remaining · CISO notified</div>
            <div className="text-[9px] text-[#F59E0B] mt-0.5">Audit window · SOC 2 PI1 re-examination pending</div>
          </div>
        </div>
        <div className="text-[10px] text-[#475569] mt-2">12 AI systems monitored · 47,832 decisions recorded this month · 3 systems with active integrity review · 1 with regulatory exposure</div>
      </div>

      {/* Consolidated Governance Alerts */}
      <GovernanceAlerts />

      {/* Active Investigation Banner */}
      <div className="rounded-xl bg-[#EF4444]/5 border border-[#EF4444]/20 px-5 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="active-investigation-card">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
          <div>
            <div className="text-xs font-semibold text-[#F8FAFC]">ACTIVE INVESTIGATION</div>
            <div className="text-[10px] text-[#94A3B8]">Legal Document Drafting · Unauthorized model change · 8,203 decisions · Apr 15, 2026</div>
            <div className="text-[9px] text-[#64748B] mt-0.5">Proof Chain Status: Classification ✅ · Control State at Decision Time <span className="text-[#F59E0B]">⚠ root cause</span> · Decision ✅ · Oversight ✅ · Package pending</div>
          </div>
        </div>
        <Link to="/dashboard/incident-review" className="text-xs text-[#EF4444] hover:text-[#F87171] font-medium flex items-center gap-1 flex-shrink-0">
          Continue investigation <ArrowRight size={12} />
        </Link>
      </div>

      {/* Governance Proof Chain Status Strip */}
      <div className="glass-card p-5 border-l-4 border-l-[#6366F1]/20" data-testid="proof-chain-status-strip">
        <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-[0.12em] mb-3">Governance Proof Chain Status · Chain Integrity Engine · Apr 15, 2026 · 14:32 UTC</div>
        <div className="grid grid-cols-5 gap-3">
          <Link to="/dashboard/proof-chain" className="p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] hover:border-[#6366F1]/30 transition-colors">
            <div className="text-[10px] text-[#475569] uppercase tracking-wider">Classification</div>
            <div className="text-sm font-bold text-[#10B981]">◆ 4/5 current</div>
            <div className="text-[9px] text-[#EF4444]">○ 1 UNAUDITED</div>
          </Link>
          <div onClick={() => window.location.href='/dashboard/proof-chain'} className="p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] hover:border-[#6366F1]/30 transition-colors cursor-pointer">
            <div className="text-[10px] text-[#475569] uppercase tracking-wider">Control State</div>
            <div className="text-sm font-bold text-[#10B981]">◆ 94% covered</div>
            <div className="text-[9px] text-[#F59E0B]"><span className="text-[#F59E0B]">↓</span> was 97% Apr 12 · <span className="text-[#6366F1]">Compare history</span></div>
          </div>
          <Link to="/dashboard/decision-ledger" className="p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] hover:border-[#6366F1]/30 transition-colors">
            <div className="text-[10px] text-[#475569] uppercase tracking-wider">Decisions</div>
            <div className="text-sm font-bold text-[#10B981]">◆ 47,832 signed</div>
            <div className="text-[9px] text-[#64748B]"><span className="text-[#64748B]">→</span> stable</div>
          </Link>
          <Link to="/dashboard/proof-chain" className="p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] hover:border-[#6366F1]/30 transition-colors">
            <div className="text-[10px] text-[#475569] uppercase tracking-wider">Oversight</div>
            <div className="text-sm font-bold text-[#F59E0B]">⚠ 3 signals</div>
            <div className="text-[9px] text-[#F59E0B]"><span className="text-[#F59E0B]">↓</span> degraded Apr 15</div>
          </Link>
          <Link to="/dashboard/assurance" className="p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] hover:border-[#6366F1]/30 transition-colors">
            <div className="text-[10px] text-[#475569] uppercase tracking-wider">Packages</div>
            <div className="text-sm font-bold text-[#10B981]">◆ 6 current</div>
            <div className="text-[9px] text-[#10B981]"><span className="text-[#10B981]">↑</span> restored Apr 17</div>
          </Link>
        </div>
      </div>

      {/* Hero stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* AI Posture Score */}
        <div className="glass-card p-6" data-testid="stat-posture-score">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[#64748B] text-xs font-medium uppercase tracking-wider mb-1">AI Posture Score</div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-[#F8FAFC]">{overviewStats.aiPostureScore}</span>
                <span className="text-[#64748B] text-sm">/ 100</span>
              </div>
              <div className="flex items-center gap-1 mt-1">
                <TrendingUp size={12} className="text-[#10B981]" />
                <span className="text-[#10B981] text-xs font-medium">+{overviewStats.aiPostureChange} this week</span>
              </div>
              <div className="text-[8px] text-[#475569] mt-1">Based on Control State at Decision Time across 5 workflows</div>
              <div className="mt-2 p-2 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
                <div className="text-[8px] text-[#475569]">Latest Control State: Apr 15, 2026 · 14:32 UTC</div>
                <div className="text-[9px] text-[#F8FAFC] font-mono">CSS-2026-047832 · <span className="text-[#10B981]">◆ AUDIT-GRADE</span> · All controls active</div>
                <Link to="/dashboard/proof-chain" className="text-[8px] text-[#6366F1] hover:text-[#818cf8]">View Control State →</Link>
              </div>
            </div>
            <div className="w-16 h-16">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={posturePieData} cx="50%" cy="50%" innerRadius={22} outerRadius={30} startAngle={90} endAngle={-270} dataKey="value" stroke="none">
                    <Cell fill="#10B981" />
                    <Cell fill="#1E2A45" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Active AI Models */}
        <div className="glass-card p-6" data-testid="stat-active-models">
          <div className="text-[#64748B] text-xs font-medium uppercase tracking-wider mb-1">Active AI Models</div>
          <div className="text-3xl font-bold text-[#F8FAFC] mb-2">{overviewStats.activeModels.total}</div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#EF4444]" />
              <span className="text-[#94A3B8]">{overviewStats.activeModels.high} high</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
              <span className="text-[#94A3B8]">{overviewStats.activeModels.medium} medium</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#10B981]" />
              <span className="text-[#94A3B8]">{overviewStats.activeModels.low} low</span>
            </span>
          </div>
        </div>

        {/* Decision Records */}
        <div className="glass-card p-6" data-testid="stat-decision-records">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[#64748B] text-xs font-medium uppercase tracking-wider mb-1">Decision Records</div>
              <div className="text-3xl font-bold text-[#F8FAFC]">{overviewStats.decisionRecords.total.toLocaleString()}</div>
              <div className="text-[#64748B] text-xs mt-1">{overviewStats.decisionRecords.monthLabel}</div>
            </div>
            <div className="w-24 h-12">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={decisionTrend}>
                  <defs>
                    <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="count" stroke="#6366F1" strokeWidth={1.5} fill="url(#sparkGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Open Compliance Gaps */}
        <div className="glass-card p-6" data-testid="stat-compliance-gaps">
          <div className="text-[#64748B] text-xs font-medium uppercase tracking-wider mb-1">Open Compliance Gaps</div>
          <div className="text-3xl font-bold text-[#F8FAFC]">{overviewStats.complianceGaps.total}</div>
          <div className="text-[#EF4444] text-xs font-medium mt-1">{overviewStats.complianceGaps.critical} critical</div>
          <div className="flex gap-1 mt-2 h-2 rounded-full overflow-hidden bg-[#1E2A45]">
            <div className="bg-[#EF4444] rounded-full" style={{ width: `${(overviewStats.complianceGaps.critical / overviewStats.complianceGaps.total) * 100}%` }} />
            <div className="bg-[#F59E0B] rounded-full" style={{ width: `${(overviewStats.complianceGaps.medium / overviewStats.complianceGaps.total) * 100}%` }} />
          </div>
        </div>
      </div>

      {/* Time-to-value banner */}
      <div className="rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20 px-5 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="ttv-banner" style={{ backgroundImage: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(99,102,241,0.02) 100%)' }}>
        <div className="text-xs text-[#F8FAFC]">
          First Decision Evidence Record generated in under <span className="font-bold text-[#6366F1]">7 days</span> from integration · <span className="font-mono font-bold">47,832</span> records captured this month · <span className="font-bold text-[#10B981]">94%</span> of AI invocations covered
        </div>
        <Link to="/dashboard/integrations" className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1 font-medium flex-shrink-0">
          How we deploy <ArrowRight size={12} />
        </Link>
      </div>

      {/* Jurisdiction urgency banner */}
      <div className="rounded-xl bg-[#0F766E]/10 border border-[#0F766E]/20 px-5 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="urgency-banner">
        <div className="text-xs text-[#94A3B8] flex-1 min-w-0">{urgencyMessages[urgencyRegion]}</div>
        <select data-testid="urgency-region" value={urgencyRegion} onChange={(e) => setUrgencyRegion(e.target.value)}
          className="px-2 py-1 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-[10px] text-[#94A3B8] focus:outline-none focus:border-[#0F766E] flex-shrink-0">
          <option value="global">Global</option>
          <option value="eu">EU</option>
          <option value="us">US</option>
          <option value="ca">Canada</option>
        </select>
      </div>

      {/* Regulatory Readiness */}
      <div className="glass-card p-6" data-testid="regulatory-readiness">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[#F8FAFC] font-semibold text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Regulatory Readiness
          </h3>
        </div>
        <div className="space-y-5">
          {regulatoryReadiness.map((reg) => (
            <div key={reg.id} className="flex items-center gap-4">
              <div className="w-32 text-sm text-[#F8FAFC] font-medium flex-shrink-0 hidden sm:block">{reg.name}</div>
              <div className="sm:hidden text-xs text-[#F8FAFC] font-medium flex-shrink-0 w-20">{reg.name}</div>
              <div className="flex-1 h-2.5 rounded-full bg-[#1E2A45] overflow-hidden">
                <div
                  className="h-full rounded-full progress-bar-fill"
                  style={{
                    width: `${reg.progress}%`,
                    backgroundColor: reg.status === 'compliant' ? '#10B981' : '#F59E0B',
                  }}
                />
              </div>
              <div className="w-12 text-right text-sm font-mono" style={{ color: reg.status === 'compliant' ? '#10B981' : '#F59E0B' }}>
                {reg.progress}%
              </div>
              <div className="w-52 text-xs text-[#64748B] flex-shrink-0 hidden lg:block">{reg.deadline}</div>
              <Link
                to="/dashboard/compliance"
                data-testid={`view-gaps-${reg.id}`}
                className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1 flex-shrink-0"
              >
                View gaps <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">        {/* Recent Decision Records */}
        <div className="lg:col-span-3 glass-card p-6" data-testid="recent-decisions">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#F8FAFC] font-semibold text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Recent Decision Records
            </h3>
            <Link
              to="/dashboard/decision-ledger"
              className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1"
            >
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2A45]">
                  <th className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2 pr-4">Time</th>
                  <th className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2 pr-4">Model</th>
                  <th className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2 pr-4">Workflow</th>
                  <th className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2 pr-4">Risk</th>
                  <th className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2 pr-4">Output</th>
                  <th className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentDecisionRecords.slice(0, 5).map((r, i) => (
                  <tr key={i} className="border-b border-[#1E2A45]/50 table-row-interactive">
                    <td className="py-2.5 pr-4 font-mono text-[#94A3B8] text-xs">{r.time}</td>
                    <td className="py-2.5 pr-4 text-[#F8FAFC]">{r.model}</td>
                    <td className="py-2.5 pr-4 text-[#94A3B8]">{r.workflow}</td>
                    <td className="py-2.5 pr-4"><RiskBadge tier={r.riskTier} /></td>
                    <td className="py-2.5 pr-4 text-[#94A3B8]">{r.outputType}</td>
                    <td className="py-2.5">
                      <span className={`text-xs font-medium ${r.sigStatus === 'Verified' ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                        {r.sigStatus === 'Verified' ? '✓' : '⚠'} {r.sigStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Risk Events */}
        <div className="lg:col-span-2 space-y-3" data-testid="risk-events">
          <h3 className="text-[#F8FAFC] font-semibold text-base mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Risk Events
          </h3>
          {riskEvents.slice(0, 2).map((event, i) => {
            const Icon = event.severity === 'critical' ? AlertCircle : event.severity === 'warning' ? AlertTriangle : Info;
            return (
              <div
                key={i}
                data-testid={`risk-event-${event.severity}`}
                className="glass-card p-4"
                style={{ borderLeftWidth: '3px', borderLeftColor: event.color }}
              >
                <div className="flex items-start gap-3">
                  <Icon size={16} style={{ color: event.color }} className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: event.color }}>
                      {event.severity}
                    </div>
                    <div className="text-sm text-[#F8FAFC] font-medium mb-1">{event.title}</div>
                    <div className="text-xs text-[#64748B] mb-2">{event.desc}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[#475569]">{event.time}</span>
                      <button
                        data-testid={`risk-event-action-${event.severity}`}
                        onClick={() => toast.info(`${event.action}: ${event.title}`, { description: 'Navigating to investigation...' })}
                        className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1"
                      >
                        {event.action} <ArrowRight size={10} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <Link to="/dashboard/regulatory-moment" data-testid="see-how-it-works" className="glass-card p-3 flex items-center justify-center gap-2 text-xs text-[#6366F1] hover:text-[#818cf8] font-medium transition-colors hover:border-[#6366F1]/30">
            See how it works <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
